<script>

    import { onMount } from 'svelte';
    import { checkPWA } from '../store';
    import { docData } from 'rxfire/firestore';
    import { db } from '../firebase';

    onMount(() => {
        /*
                Making sure PWA is installed and running from home screen is tricky!
                
                Originea's experience heavily relies on mobile screens and touch interactions. 
                Therefore, we don't want to allow desktop access. That's the easy part.
    
                Now to achive an app-like experience, we want users to install Originea to the homescreen of their phone.
                Right now, (Feb 2021) you can trigger the event beforeInstallprompt on Android (All Chromium Browsers & Firefox).
                So a simple button click will add Orginea to the homescreen. Yay!
    
                On iOS things are trickier. There is no beforeInstallprompt event right now. So you need to show "instructions" to the user like:
    
                1. Click on share
                2. Add to homescreen
    
                And now the fun part: If someone is using Chrome, Firefox or Brave on iOS there is no "Add to homescreen" feature visible within the "share" popover or anywhere else in the Browser.
                The same issue appears, if the PWA is launched in a Webview / In-App Browser. No A2HS even if you are using Safari as your default browser.
            
                This means, you have to determine if the user needs to open "the real" Safari first. 
                Or if this is not possible, just tell them to use Safari and let them pass without A2HS.
    
                So here are the things to do:
    
                1. Check if mobile device
                2. Check if already PWA is already installed and running in standalone mode
                3. Check if beforeInstallpromt is available
                    3.1 If available just add it to homescreen and replace button with "See you there."
                    3.2 If not available go to 3.
                4. Check if iOS
                    4.1 If iOS - Check if Browser-Tab or Webview
                    4.2 If iOS and Webview - Add "Open in Browser" to instructions
                5. If everything fails, show general instructions to add it to homescreen with option "Continue in Browser"
            */
    
        checkVersioning(); // Check if there is a new Version of the App available on Firebase.
        checkServiceWorker(); // Check if service worker is available and install / update it.
    
        // Check how PWA was launched
        if(checkMobile()) // if true go ahead, if false stop executing since it's a desktop
            if(checkNotStandaloneYet()) // if true go ahead, if false do nothing since it's already running in PWA mode
                checkBeforeInstallPrompt().catch(()=> { // if it resolves install button will be shown, if false PWA is already installed but not in standalone, show "Launch from Homescreen." OR beforeinstallPrompt event is not available
                    checkBrowserAndWebView(); // Finally determine what instructions need to be shown if any of the above checks failed
                })
    })

    // Check if PWA was launched from home screen or not.
    function checkNotStandaloneYet() {
        if (navigator.standalone || window.matchMedia('(display-mode: standalone)').matches) {
            return false;
        } else {
            return true;
        }
    }

    // Check if service worker is available and install / update it.
    function checkServiceWorker() {
        if('serviceWorker' in navigator) {
            navigator.serviceWorker
            .register('service-worker.js', { scope: './' })
            .then(function(registration) {
                console.log('Service worker registered.')
            });
        }
    }

    // Check if desktop or mobile
    function checkMobile() {
        let hasTouchScreen = false;
        // Mobile check based on Mozillas recommendations:
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
        if ("maxTouchPoints" in navigator) {
            hasTouchScreen = navigator.maxTouchPoints > 0;
        } else if ("msMaxTouchPoints" in navigator) {
            hasTouchScreen = navigator.msMaxTouchPoints > 0;
        } else {
            let mQ = window.matchMedia && matchMedia("(pointer:coarse)");

            if (mQ && mQ.media === "(pointer:coarse)") {
                hasTouchScreen = !!mQ.matches;  
            } else if ('orientation' in window) {
                hasTouchScreen = true; // deprecated, but good fallback
            } else {
                // Only as a last resort, fall back to user agent sniffing
                let UA = navigator.userAgent;
                hasTouchScreen = (
                    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
                    /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
                );
            }
        }
        $checkPWA.isMobile = hasTouchScreen;
        return hasTouchScreen;
    }

    // Check browser
    function checkBrowserAndWebView() {
        let currentBrowserView = null;

        const   standalone = navigator.standalone,
                userAgent = navigator.userAgent.toLowerCase(),
                safari = /safari/.test(userAgent),
                ios = /iphone|ipod|ipad/.test(userAgent);
        
        // Since every Browser on iOS is Safari behind the scenes, there is no need to check for other browsers.
        if (ios) {
            if (!standalone && safari) {
                // Safari iOS (...and unfortunately Twitter In-App Webview)
                // https://leemartin.medium.com/twitter-please-identify-yourself-in-your-app-webview-user-agent-4f58dbadfa05)
                currentBrowserView = 'iOSBrowserTab';
            } else if (!standalone && !safari) {
                // iOS webview
                currentBrowserView = 'iOSWebView';
            }
        } else {
            // Most likely Android or another mobile OS
            if (userAgent.includes('wv')) {
                // Android webview
                currentBrowserView = 'AndroidWebView';
            } else {
                // Chrome, Brave etc.
                currentBrowserView = 'AndroidBrowserTab';
            }
        }
        console.log('Guess you are browsing in: ' + currentBrowserView);
        $checkPWA.instructionsToShow = currentBrowserView;
        return currentBrowserView;
    }

    // Check if new version of the App is availabe and prompt user to reload
    function checkVersioning() {
        const build = docData(db.collection('versioning').doc('version'));
        build.subscribe((data) => {
            if( checkLocalStorage() && Object.keys(data).length !== 0) {
                if(localStorage.getItem('build') ){
                    if(localStorage.getItem('build') === data.build) {
                        console.log('You already have the latest version.');
                        return
                    }else {
                        localStorage.setItem('build', data.build);
                        alert('New version available! Need to reload.');
                        window.location.reload();
                    }
                }else {
                    localStorage.setItem('build', data.build);
                }
            }
        });
    }

    // Check if localstorage is available
    function checkLocalStorage () {
        try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            return true;
        } catch(e) {
            return false;
        }
    }

    // Register beforeinstallprompt 
    function checkBeforeInstallPrompt() {
        return new Promise((resolve, reject) => {
            if ("BeforeInstallPromptEvent" in window) {
                window.addEventListener('beforeinstallprompt', (e) => {
                    // Prevent Chrome 67 and earlier from automatically showing the prompt
                    e.preventDefault();
                    // Stash the event so it can be triggered later.
                    $checkPWA.deferredPrompt = e;
                    $checkPWA.instructionsToShow = 'installPrompt';
                    console.log('Install prompt available.');
                    resolve();
                });
            } else {
                console.log('Already installed or event not available.')
                reject();
            }
        });
    }
</script>